import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  CalendarDays,
  LayoutDashboard,
  LifeBuoy,
  CheckSquare,
  UserSearch,
  Building2,
  Handshake,
  Wrench,
  Package,
  Truck,
  LogOut,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Lightbulb,
  Sparkles,
  ShoppingCart,
  ShoppingBag,
  FileText,
  Settings,
  Warehouse,
  FolderOpen,
  Mail,
  Bell,
  BellDot,
  MessageCircle,
  ScanLine,
  BookUser,
  Briefcase,
  Inbox,
  Library,
  LayoutGrid,
  Palette,
  UserRound,
  Activity,
  PackageCheck,
} from "lucide-react";
import { getRoleLevel } from "@shared/roles";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { SuggestionModal } from "@/components/suggestion-modal";
import FeedbackForm from "@/components/feedback-form";
import { ChangelogDrawer, getChangelogUnread } from "@/components/changelog-drawer";
import { RemindersPanel, useReminderCount } from "@/components/reminders-panel";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { authFetch } from "@/lib/authFetch";
import type { ChangelogEntry } from "@shared/schema";

interface LayoutShellProps {
  children: React.ReactNode;
}

// minLevel correspond au niveau ROLE_LEVEL requis dans shared/roles.ts
// (technicien=1, manager=2, admin=3, super_admin=4)
// Ne plus utiliser `roles` (valeurs métier) pour décider de l'accès.
const NAV_GROUPS = [
  {
    label: "",
    items: [
      { name: "Procédures", href: "/procedures", icon: Library, minLevel: 1 },
      { name: "Activité", href: "/activite", icon: Activity, minLevel: 1, isActivityFeed: true },
    ],
  },
  {
    label: "Opérationnel",
    items: [
      { name: "Missions", href: "/missions", icon: CalendarDays, minLevel: 1 },
      { name: "Planning", href: "/planning", icon: LayoutGrid, minLevel: 1 },
      { name: "Tickets", href: "/tickets", icon: LifeBuoy, minLevel: 1 },
      { name: "Chats SAV", href: "/chats", icon: MessageCircle, minLevel: 1, isChatBadge: true },
      { name: "Suivi des actions", href: "/tasks", icon: CheckSquare, minLevel: 1 },
      { name: "Rappels", href: "/rappels", icon: Bell, minLevel: 1 },
      { name: "Préparation de commande", href: "/preparation", icon: PackageCheck, minLevel: 1 },
      { name: "Sous-traitance", href: "/sous-traitance", icon: Briefcase, minLevel: 2 },
      { name: "Recrutement", href: "/rh/candidats", icon: UserRound, minLevel: 2 },
    ],
  },
  {
    label: "Commercial",
    items: [
      { name: "Commandes", href: "/commandes", icon: ShoppingCart, minLevel: 2 },
      { name: "Achats", href: "/achats", icon: ShoppingBag, minLevel: 2 },
      { name: "Devis", href: "/devis", icon: FileText, minLevel: 1 },
      { name: "Prospects", href: "/prospects", icon: UserSearch, minLevel: 1 },
      { name: "Clients", href: "/clients", icon: Building2, minLevel: 1 },
      { name: "Revendeurs", href: "/resellers", icon: Handshake, minLevel: 1 },
      { name: "Contacts", href: "/contacts", icon: BookUser, minLevel: 1 },
    ],
  },
  {
    label: "Technique",
    items: [
      { name: "Robots", href: "/robots", icon: Bot, minLevel: 1 },
      { name: "SAV", href: "/sav", icon: Wrench, minLevel: 1 },
      { name: "Produits", href: "/products", icon: Package, minLevel: 1 },
      { name: "Stock", href: "/stock", icon: Warehouse, minLevel: 2 },
      { name: "Véhicules", href: "/vehicles", icon: Truck, minLevel: 2 },
    ],
  },
  {
    label: "Paramètres",
    items: [
      { name: "Agences", href: "/parametres/agences", icon: Building2, minLevel: 3 },
      { name: "Paramètres", href: "/parametres", icon: Settings, minLevel: 4 },
      { name: "Espace partenaires", href: "/parametres/partenaires", icon: FolderOpen, minLevel: 4 },
      { name: "Emails", href: "/parametres/emails", icon: Mail, minLevel: 4 },
      { name: "Rappels (config.)", href: "/parametres/rappels", icon: Bell, minLevel: 4 },
      { name: "Types de mission", href: "/parametres/types-mission", icon: Palette, minLevel: 4 },
      { name: "Feedback & Suggestions", href: "/admin/feedback", icon: Lightbulb, minLevel: 4 },
    ],
  },
];

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem("sidebar-collapsed") === "true";
  } catch {
    return false;
  }
}

export function LayoutShell({ children }: LayoutShellProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsed);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [isRemindersOpen, setIsRemindersOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const { data: reminderCountData } = useReminderCount();
  const reminderCount = reminderCountData?.count || 0;
  const { data: chatCountsData } = useQuery<{ nouveau: number }>({
    queryKey: ["/api/chats/counts"],
    queryFn: async () => (await authFetch("/api/chats/counts")).json(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const chatNewCount = chatCountsData?.nouveau || 0;
  const isMobile = useIsMobile();
  const { profile, logout } = useAuth();
  const [location, navigate] = useLocation();
  const queryClientHook = useQueryClient();
  const { data: notifCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/count"],
    queryFn: async () => (await authFetch("/api/notifications/count")).json(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const notifCount = notifCountData?.count || 0;
  const { data: notifications = [] } = useQuery<any[]>({
    queryKey: ["/api/notifications"],
    queryFn: async () => (await authFetch("/api/notifications")).json(),
    enabled: isNotifOpen,
    staleTime: 10_000,
  });
  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await authFetch("/api/notifications/read-all", { method: "PUT" });
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ["/api/notifications/count"] });
      queryClientHook.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  const { data: changelogEntries = [] } = useQuery<ChangelogEntry[]>({
    queryKey: ["/api/changelog"],
    queryFn: async () => (await authFetch("/api/changelog")).json(),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    setHasUnread(getChangelogUnread(changelogEntries));
  }, [changelogEntries]);

  const openChangelog = () => {
    setIsChangelogOpen(true);
    setHasUnread(false);
  };

  const effectiveCollapsed = isCollapsed && !isMobile;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    try {
      localStorage.setItem("sidebar-collapsed", String(next));
    } catch {}
  };

  // Accès piloté exclusivement par accessRole — profile.role (métier) n'est plus utilisé ici.
  const userLevel = getRoleLevel(profile?.accessRole);

  const { data: activityData } = useQuery<{ rows: any[]; total: number }>({
    queryKey: ["/api/activity", "recent-check"],
    queryFn: async () => (await authFetch("/api/activity?limit=1&offset=0")).json(),
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  });
  const hasRecentActivity = activityData?.rows?.length
    ? Date.now() - new Date(activityData.rows[0].createdAt).getTime() < 24 * 3600 * 1000
    : false;

  const filteredGroups = NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(item => userLevel >= (item.minLevel ?? 1)),
    }))
    .filter(group => group.items.length > 0);

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row font-sans text-foreground">
      {/* Mobile header */}
      <header className="md:hidden h-16 border-b flex items-center justify-between px-4 bg-card z-50 sticky top-0">
        <div className="flex items-center gap-2">
          <Bot className="w-6 h-6 text-primary" />
          <span className="font-bold text-lg tracking-tight">Jobotech</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsScannerOpen(true)}
            data-testid="button-open-scanner-mobile"
            title="Scanner un robot"
          >
            <ScanLine className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => { setIsNotifOpen(!isNotifOpen); }}
            data-testid="button-open-notifications-mobile"
          >
            <Inbox className="w-5 h-5" />
            {notifCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 text-[10px] font-bold bg-blue-500 text-white rounded-full flex items-center justify-center px-0.5">
                {notifCount > 99 ? "99+" : notifCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setIsRemindersOpen(true)}
            data-testid="button-open-reminders-mobile"
          >
            <Bell className="w-5 h-5" />
            {reminderCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center px-0.5">
                {reminderCount > 99 ? "99+" : reminderCount}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="icon" className="relative" onClick={openChangelog} data-testid="button-open-changelog-mobile">
            <Sparkles className="w-5 h-5" />
            {hasUnread && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleSidebar} data-testid="button-toggle-sidebar">
            {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <TooltipProvider delayDuration={0}>
        <aside className={cn(
          "fixed inset-y-0 left-0 z-40 bg-card border-r shadow-sm transform transition-all duration-300 ease-in-out md:translate-x-0 md:static md:h-screen",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
          effectiveCollapsed ? "w-16" : "w-64"
        )}>
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className={cn(
              "h-20 flex items-center border-b border-border/50 shrink-0",
              effectiveCollapsed ? "px-3 justify-center" : "px-6 justify-between"
            )}>
              {!effectiveCollapsed && (
                <div className="flex items-center gap-3">
                  <Bot className="w-8 h-8 text-primary shrink-0" />
                  <div>
                    <h1 className="font-bold text-xl tracking-tight">Jobotech</h1>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Operations</p>
                  </div>
                </div>
              )}
              {effectiveCollapsed && <Bot className="w-8 h-8 text-primary shrink-0" />}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleCollapsed}
                className={cn(
                  "hidden md:flex shrink-0 text-muted-foreground hover:text-foreground",
                  effectiveCollapsed && "mx-auto"
                )}
                data-testid="button-toggle-collapsed"
              >
                {effectiveCollapsed
                  ? <PanelLeftOpen className="w-4 h-4" />
                  : <PanelLeftClose className="w-4 h-4" />
                }
              </Button>
            </div>

            {/* Navigation */}
            <nav className={cn(
              "flex-1 py-4 overflow-y-auto space-y-5",
              effectiveCollapsed ? "px-2" : "px-3"
            )} data-testid="nav-sidebar">
              {/* Tableau de bord */}
              <div className="space-y-0.5">
                {(() => {
                  const isActive = location === "/";
                  return (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          href="/"
                          onClick={() => setIsSidebarOpen(false)}
                          className={cn(
                            "flex items-center rounded-lg text-sm font-medium transition-all duration-200 group relative",
                            effectiveCollapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
                            isActive
                              ? "bg-primary/10 text-primary"
                              : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                          )}
                          data-testid="nav-link-dashboard"
                        >
                          <LayoutDashboard className={cn(
                            "w-[18px] h-[18px] shrink-0 transition-colors",
                            isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                          )} />
                          {!effectiveCollapsed && (
                            <>
                              <span className="truncate">Tableau de bord</span>
                              {isActive && <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-primary" />}
                            </>
                          )}
                          {effectiveCollapsed && isActive && (
                            <div className="absolute right-1 top-1 w-1.5 h-1.5 rounded-full bg-primary" />
                          )}
                        </Link>
                      </TooltipTrigger>
                      {effectiveCollapsed && (
                        <TooltipContent side="right" className="font-medium">Tableau de bord</TooltipContent>
                      )}
                    </Tooltip>
                  );
                })()}
              </div>

              {filteredGroups.map((group) => (
                <div key={group.label}>
                  {!effectiveCollapsed && (
                    <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 select-none" data-testid={`nav-group-${group.label.toLowerCase()}`}>
                      {group.label}
                    </p>
                  )}
                  {effectiveCollapsed && <div className="h-px bg-border/40 mx-1 mb-2" />}
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const isActive = location === item.href || location.startsWith(item.href + "/") || location.startsWith(item.href + "?");
                      const badgeCount = (item as any).isChatBadge ? chatNewCount : 0;
                      const showActivityDot = (item as any).isActivityFeed && hasRecentActivity && !isActive;
                      return (
                        <Tooltip key={item.href}>
                          <TooltipTrigger asChild>
                            <Link
                              href={item.href}
                              onClick={() => setIsSidebarOpen(false)}
                              className={cn(
                                "flex items-center rounded-lg text-sm font-medium transition-all duration-200 group relative",
                                effectiveCollapsed ? "justify-center px-2 py-2" : "gap-3 px-3 py-2",
                                isActive
                                  ? "bg-primary/10 text-primary"
                                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                              )}
                              data-testid={`nav-link-${item.href.replace("/", "")}`}
                            >
                              <div className="relative shrink-0">
                                <item.icon className={cn(
                                  "w-[18px] h-[18px] transition-colors",
                                  isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                                )} />
                                {effectiveCollapsed && badgeCount > 0 && (
                                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 text-[9px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center px-0.5">
                                    {badgeCount > 99 ? "99+" : badgeCount}
                                  </span>
                                )}
                                {effectiveCollapsed && showActivityDot && (
                                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-primary" data-testid="dot-activity-recent" />
                                )}
                              </div>
                              {!effectiveCollapsed && (
                                <>
                                  <span className="truncate flex-1">{item.name}</span>
                                  {badgeCount > 0 && (
                                    <span className="min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center px-1 shrink-0">
                                      {badgeCount > 99 ? "99+" : badgeCount}
                                    </span>
                                  )}
                                  {showActivityDot && badgeCount === 0 && (
                                    <span className="w-2 h-2 rounded-full bg-primary shrink-0" data-testid="dot-activity-recent" />
                                  )}
                                  {isActive && badgeCount === 0 && !showActivityDot && <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-primary" />}
                                </>
                              )}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right" className={cn(!effectiveCollapsed && "md:hidden")}>
                            {item.name}{badgeCount > 0 ? ` (${badgeCount})` : ""}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>

            {/* Zone utilisateur */}
            <div className={cn(
              "border-t border-border/50 bg-secondary/30",
              effectiveCollapsed ? "p-2" : "p-4"
            )}>
              {!effectiveCollapsed ? (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="h-9 w-9 border-2 border-background shadow-sm shrink-0">
                      <AvatarImage src={profile?.avatarUrl || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                        {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">
                        {profile?.firstName} {profile?.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground truncate capitalize">
                        {({ admin: "Administrateur", planification: "Planification", technicien: "Technicien", commercial: "Commercial", administration: "Administration" } as Record<string, string>)[profile?.role || ""] || profile?.role}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="flex-1 text-muted-foreground hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950/30"
                          onClick={() => setIsSuggestionOpen(true)}
                          data-testid="button-open-suggestion"
                        >
                          <Lightbulb className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Faire une suggestion</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="flex-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={logout}
                          data-testid="button-logout"
                        >
                          <LogOut className="w-4 h-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">Déconnexion</TooltipContent>
                    </Tooltip>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="h-9 w-9 border-2 border-background shadow-sm cursor-pointer">
                        <AvatarImage src={profile?.avatarUrl || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                          {profile?.firstName?.[0]}{profile?.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="right">{profile?.firstName} {profile?.lastName}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950/30"
                        onClick={() => setIsSuggestionOpen(true)}
                        data-testid="button-open-suggestion-collapsed"
                      >
                        <Lightbulb className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Faire une suggestion</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={logout}
                        data-testid="button-logout-collapsed"
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Déconnexion</TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </aside>
      </TooltipProvider>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-background transition-all duration-300">
        {/* Barre de navigation desktop avec Scanner + Notifications + Rappels + Nouveautés */}
        <div className="hidden md:flex items-center justify-end gap-1 px-8 py-3 border-b border-border/30 bg-card/50 shrink-0 relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={() => setIsScannerOpen(true)}
                data-testid="button-open-scanner-desktop"
              >
                <ScanLine className="w-5 h-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Scanner un robot</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={() => setIsNotifOpen(v => !v)}
                data-testid="button-open-notifications"
              >
                <Inbox className="w-5 h-5" />
                {notifCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 text-[10px] font-bold bg-blue-500 text-white rounded-full flex items-center justify-center px-0.5">
                    {notifCount > 99 ? "99+" : notifCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Notifications</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={() => setIsRemindersOpen(true)}
                data-testid="button-open-reminders"
              >
                <Bell className="w-5 h-5" />
                {reminderCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[16px] h-4 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center px-0.5">
                    {reminderCount > 99 ? "99+" : reminderCount}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Rappels</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-muted-foreground hover:text-primary hover:bg-primary/10"
                onClick={openChangelog}
                data-testid="button-open-changelog"
              >
                <Sparkles className="w-5 h-5" />
                {hasUnread && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Nouveautés</TooltipContent>
          </Tooltip>
          {isNotifOpen && (
            <div className="absolute top-full right-2 mt-1 w-80 max-h-96 bg-card border rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b">
                <span className="font-semibold text-sm">Notifications</span>
                {notifCount > 0 && (
                  <button
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    onClick={() => markAllReadMutation.mutate()}
                  >
                    Tout marquer lu
                  </button>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">Aucune notification</div>
                ) : notifications.map((n: any) => {
                  const handleNotifClick = async () => {
                    if (!n.read) {
                      await authFetch(`/api/notifications/${n.id}/read`, { method: "PUT" }).catch(() => {});
                      queryClientHook.invalidateQueries({ queryKey: ["/api/notifications/count"] });
                      queryClientHook.invalidateQueries({ queryKey: ["/api/notifications"] });
                    }
                    setIsNotifOpen(false);
                    if (n.bonLivraisonId) {
                      navigate(`/preparation/${n.bonLivraisonId}`);
                    } else if (n.ticketId) {
                      navigate(`/tickets?openId=${n.ticketId}`);
                    } else if (n.taskId) {
                      navigate(`/tasks?taskId=${n.taskId}`);
                    } else {
                      navigate("/tasks");
                    }
                  };
                  return (
                    <button
                      key={n.id}
                      className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${!n.read ? "bg-blue-50/60 dark:bg-blue-950/20" : ""}`}
                      onClick={handleNotifClick}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <p className={`text-sm ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.message}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{new Date(n.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto animate-in fade-in duration-500 slide-in-from-bottom-4">
            {children}
          </div>
        </div>
      </main>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <SuggestionModal
        isOpen={isSuggestionOpen}
        onClose={() => setIsSuggestionOpen(false)}
        userName={`${profile?.firstName || ""} ${profile?.lastName || ""}`.trim()}
      />
      <ChangelogDrawer
        isOpen={isChangelogOpen}
        onClose={() => setIsChangelogOpen(false)}
      />
      <RemindersPanel
        isOpen={isRemindersOpen}
        onClose={() => setIsRemindersOpen(false)}
      />
      {isScannerOpen && (
        <BarcodeScanner onClose={() => setIsScannerOpen(false)} />
      )}
      <FeedbackForm floating />
    </div>
  );
}
